import {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DATE_FORMAT_ISO, parse } from '@eneco/common/date';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { FeatureList } from '@eneco/components/ui/FeatureList/FeatureList';
import { Header, NextButton } from '@eneco/flows/components';
import { useFormatter } from '@eneco/i18n';
import { useRouter } from '@eneco/next';
import { Bleed, Box, Card, Heading, NavLink, Stack, Text, TextLink } from '@eneco/sparky';
import {
  CalendarIcon,
  ClockIcon,
  DocumentIcon,
  ElectricityIcon,
  EnvelopeIcon,
  EuroIcon,
  HomeIcon,
  PhoneIcon,
  ProfileIcon,
  SettingBarsIcon,
  TruckIcon,
} from '@eneco/sparky/icons';
import { Calendar } from '@eneco/sparky/illustrations';
import { GrowthBookContext } from '@growthbook/growthbook-react';
import { usePlaceholderContent } from '@sitecore/common';
import { Fields as SummaryStepFields, SummaryStepRendering } from '@sitecore/types/SummaryStep';

import { TermsAndConditions } from '../../../../shared/components';
import { formatInitials } from '../../../../shared/helpers';
import { useSummaryStepLogic } from '../../../../shared/hooks';
import { PriceDetailsOverview } from '../../../components/PriceDetailsOverview/PriceDetailsOverview';
import { exp_n_477_B } from '../../exp-n-477/exp_n_477_B';
import { DetailList } from '../components/DetailList';
import { DetailListItem } from '../components/DetailListItem';
import { SummaryIbanField } from '../components/SummaryIbanField';

export interface SummaryFormFields {
  iban?: string;
}

export const summaryBleedHorizontal = { initial: 6, md: 10, lg: 0 } as const;
export const summaryCardCorners = { initial: 'square', lg: 'rounded' } as const;

/** Experiment summary header: fixed title, no subtitle. */
export const SummaryStepHeader: FC = () => (
  <Header>
    <Header.Title>Je overzicht</Header.Title>
  </Header>
);

type GoToStepName = 'STEP_PERSONAL_DETAILS' | 'STEP_ADDRESS' | 'STEP_MOVING' | 'STEP_CONTACT_DETAILS';

/** Shared logic for exp-n-522 summary step variants B and C. */
export const useExpN522SummaryStep = () => {
  const { SummaryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SummaryStep: SummaryStepRendering;
  }>();
  const { query } = useRouter();
  // Read GrowthBook directly: useFeatureValue throws without a provider.
  const { growthbook } = useContext(GrowthBookContext);

  const summaryStepLogic = useSummaryStepLogic<SummaryFormFields>({
    extensionMode: 'never',
    paymentDetailsTextualData: textualData?.paymentDetails,
    promotionTexts: textualData?.promotionTexts,
    requireSelectedOfferForSuccess: false,
    includeOrderedOfferInNavigation: true,
  });

  const { sendToFlowMachine, offerState, form, flowContext, selectedOffer } = summaryStepLogic;

  const { address, date } = useFormatter();

  const [isNavigating, setIsNavigating] = useState(false);
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);
  const [isContractPeriodDialogOpen, setIsContractPeriodDialogOpen] = useState(false);
  const [isConditionsDialogOpen, setIsConditionsDialogOpen] = useState(false);
  const [isTariffsDialogOpen, setIsTariffsDialogOpen] = useState(false);

  // exp-n-477 removes CONTACT_DETAILS in variants B/C, so summary contact rows must open PERSONAL_DETAILS.
  const isPersonalDetailsConsolidationActive =
    query.disableExperiments !== 'true' &&
    ['B', 'C'].includes(String(growthbook?.getFeatureValue(exp_n_477_B.testKey, '') ?? ''));

  const displayName = useMemo(() => {
    const { salutation, firstName, initials, surnamePreposition, surname } = flowContext;
    const capitalizedSalutation =
      salutation && salutation !== 'neutral' ? `${salutation.charAt(0).toUpperCase()}${salutation.slice(1)}.` : '';
    const prefix = capitalizedSalutation ? `${capitalizedSalutation} ` : '';
    const formattedInitials = initials ? formatInitials(initials) : '';
    const name = [firstName, formattedInitials ? `(${formattedInitials})` : ''].filter(Boolean).join(' ');
    const infix = surnamePreposition ? ` ${surnamePreposition}` : '';
    return `${prefix}${name}${infix} ${surname ?? ''}`.trim();
  }, [flowContext]);

  const formattedAddress = useMemo(() => {
    const { street, houseNumber, houseNumberSuffix, postalCode, city } = flowContext;
    return {
      street: address.streetAddress({ street, houseNumber, houseNumberSuffix }),
      postalCodeCity: address.postalCodeCity({ postalCode, city }),
    };
  }, [address, flowContext]);

  const contractPeriod = useMemo(() => {
    const product = selectedOffer?.products?.[0];
    const startDate = product?.contractStartDate;
    const endDate = product?.contractEndDate;
    if (!startDate || !endDate) return undefined;
    return `${date.medium(parse(startDate, DATE_FORMAT_ISO))} tot ${date.medium(parse(endDate, DATE_FORMAT_ISO))}`;
  }, [date, selectedOffer]);

  const goToStep = (stepName: GoToStepName) => {
    const resolvedStepName =
      stepName === 'STEP_CONTACT_DETAILS' && isPersonalDetailsConsolidationActive ? 'STEP_PERSONAL_DETAILS' : stepName;

    setIsNavigating(true);
    sendToFlowMachine({ type: 'GOTO', stepName: resolvedStepName });
  };

  useEffect(() => {
    // For heat only products, the offer step is skipped which causes the discount code not to be set
    // In this case we need to add the discount code to the flow context manually
    if (offerState.matches('SUCCESS') && !flowContext.discountCode) {
      sendToFlowMachine({
        type: 'UPDATE_VALUES',
        values: {
          discountCode: offerState.context.offer?.productCombinations?.[0]?.discountCode,
        },
      });
    }
  }, [offerState, flowContext.discountCode, sendToFlowMachine]);

  return {
    ...summaryStepLogic,
    textualData,
    form,
    isNavigating,
    isTermsDialogOpen,
    setIsTermsDialogOpen,
    isContractPeriodDialogOpen,
    setIsContractPeriodDialogOpen,
    isConditionsDialogOpen,
    setIsConditionsDialogOpen,
    isTariffsDialogOpen,
    setIsTariffsDialogOpen,
    displayName,
    formattedAddress,
    contractPeriod,
    goToStep,
  };
};

interface SummaryDetailsListProps {
  displayName: string;
  birthdate?: string;
  birthDateLabel?: string;
  street: string;
  postalCodeCity: string;
  contractPeriod?: string;
  emailAddress: string;
  phoneNumber: string;
  isMoving?: boolean;
  onGoToStep: (stepName: GoToStepName) => void;
  onOpenContractPeriod: () => void;
  onOpenTerms: () => void;
}

export const SummaryDetailsList: FC<SummaryDetailsListProps> = ({
  displayName,
  birthdate,
  birthDateLabel = 'Geboortedatum',
  street,
  postalCodeCity,
  contractPeriod,
  emailAddress,
  phoneNumber,
  isMoving,
  onGoToStep,
  onOpenContractPeriod,
  onOpenTerms,
}) => (
  <Bleed horizontal={summaryBleedHorizontal}>
    <DetailList corners={summaryCardCorners}>
      <DetailListItem
        icon={<ProfileIcon size="large" color="iconPrimary" />}
        title={displayName}
        description={birthdate ? `${birthDateLabel}: ${birthdate}` : undefined}
        trailingAction="arrow"
        onClick={() => onGoToStep('STEP_PERSONAL_DETAILS')}
      />
      <DetailListItem
        icon={<HomeIcon size="large" color="iconPrimary" />}
        title={street}
        description={postalCodeCity}
        descriptionWeight="bold"
      />
      {contractPeriod && (
        <DetailListItem
          icon={<CalendarIcon size="large" color="iconPrimary" />}
          title={contractPeriod}
          description="Wanneer we energie leveren"
          trailingAction="information"
          onClick={onOpenContractPeriod}
        />
      )}
      <DetailListItem
        icon={<EnvelopeIcon size="large" color="iconPrimary" />}
        title={emailAddress}
        description="Hier ontvang je je contract"
        trailingAction="arrow"
        onClick={() => onGoToStep('STEP_CONTACT_DETAILS')}
      />
      <DetailListItem
        icon={<PhoneIcon size="large" color="iconPrimary" />}
        title={phoneNumber}
        description="Voor mogelijke vragen"
        trailingAction="arrow"
        onClick={() => onGoToStep('STEP_CONTACT_DETAILS')}
      />
      <DetailListItem
        icon={<SettingBarsIcon size="large" color="iconPrimary" />}
        title="Voorkeuren"
        description="Zo houden we je op de hoogte"
        trailingAction="arrow"
        onClick={() => onGoToStep('STEP_CONTACT_DETAILS')}
      />
      <DetailListItem
        icon={<TruckIcon size="large" color="iconPrimary" />}
        title="Verhuizen"
        description="We regelen je overstap"
        trailingText={isMoving ? 'Ja' : 'Nee'}
        onClick={() => onGoToStep('STEP_MOVING')}
      />
      <DetailListItem
        icon={<DocumentIcon size="large" color="iconPrimary" />}
        title="Goed om te weten"
        description="Onze afspraken op een rijtje"
        trailingAction="arrow"
        onClick={onOpenTerms}
      />
    </DetailList>
  </Bleed>
);

interface SummaryPaymentCardProps {
  textualData?: SummaryStepFields['paymentDetails'];
  register: ReturnType<typeof useExpN522SummaryStep>['form']['register'];
  ibanError: ReturnType<typeof useExpN522SummaryStep>['form']['formState']['errors']['iban'];
  isPersonalDetailsEditProhibited?: boolean;
}

export const SummaryPaymentCard: FC<SummaryPaymentCardProps> = ({
  textualData,
  register,
  ibanError,
  isPersonalDetailsEditProhibited,
}) => (
  <Bleed horizontal={summaryBleedHorizontal}>
    <Card corners={summaryCardCorners} overflow="hidden">
      <Box padding="4">
        <Stack as="ul" gap="3">
          <DetailListItem
            icon={<EuroIcon color="iconPrimary" />}
            title="Betaling"
            description="Voor de afschrijving van je termijnbedrag"
          />
          {!isPersonalDetailsEditProhibited && (
            <SummaryIbanField textualData={textualData} register={register} ibanError={ibanError} />
          )}
        </Stack>
      </Box>
    </Card>
  </Bleed>
);

export const SummaryBenefitsList: FC = () => (
  <FeatureList
    items={[
      {
        icon: <ClockIcon color="feedbackSuccess" />,
        title: '14 dagen bedenktijd',
        description: 'Na het afsluiten kun je kosteloos annuleren. Geen risico.',
      },
      {
        icon: <TruckIcon color="feedbackSuccess" />,
        title: 'Overstap regelen wij',
        description: 'Wij zeggen je oude contract op. Jij hoeft zelf niets te doen.',
      },
      {
        icon: <ElectricityIcon color="feedbackSuccess" />,
        title: 'Altijd energie',
        description: 'Je contract gaat in op de door jou gekozen datum.',
      },
    ]}
  />
);

interface SummaryAgreementProps {
  nextStepText?: string;
  isLoading?: boolean;
  onOpenConditions: () => void;
  onOpenTariffs: () => void;
  textualData?: SummaryStepFields;
}

export const SummaryAgreement: FC<SummaryAgreementProps> = ({
  nextStepText,
  isLoading,
  onOpenConditions,
  onOpenTariffs,
  textualData,
}) => (
  <Box paddingTop="6">
    <Stack direction="column" gap="4">
      <Text size="BodyS" align="center">
        Je gaat akkoord met de <TextLink onClick={onOpenConditions}>voorwaarden</TextLink>,{' '}
        <TextLink onClick={onOpenTariffs}>tarieven</TextLink> en{' '}
        <TextLink href="https://www.edrcreditservices.nl/privacy-statement/" target="_blank">
          privacy statement
        </TextLink>{' '}
        van Eneco.
      </Text>
      <NextButton isLoading={isLoading} isFullWidth>
        {nextStepText}
      </NextButton>
      <SummaryDisputeText>{textualData?.content?.energyDisputeCommitteeText?.value}</SummaryDisputeText>
    </Stack>
  </Box>
);

interface SummaryStepDialogsProps {
  label: ReturnType<typeof useExpN522SummaryStep>['label'];
  flowContext: ReturnType<typeof useExpN522SummaryStep>['flowContext'];
  textualData?: SummaryStepFields;
  selectedOffer: ReturnType<typeof useExpN522SummaryStep>['selectedOffer'];
  selectedOfferTermsAndConditions: ReturnType<typeof useExpN522SummaryStep>['selectedOfferTermsAndConditions'];
  isTermsDialogOpen: boolean;
  setIsTermsDialogOpen: Dispatch<SetStateAction<boolean>>;
  isContractPeriodDialogOpen: boolean;
  setIsContractPeriodDialogOpen: Dispatch<SetStateAction<boolean>>;
  isConditionsDialogOpen: boolean;
  setIsConditionsDialogOpen: Dispatch<SetStateAction<boolean>>;
  isTariffsDialogOpen: boolean;
  setIsTariffsDialogOpen: Dispatch<SetStateAction<boolean>>;
}

export const SummaryStepDialogs: FC<SummaryStepDialogsProps> = ({
  label,
  flowContext,
  textualData,
  selectedOffer,
  selectedOfferTermsAndConditions,
  isTermsDialogOpen,
  setIsTermsDialogOpen,
  isContractPeriodDialogOpen,
  setIsContractPeriodDialogOpen,
  isConditionsDialogOpen,
  setIsConditionsDialogOpen,
  isTariffsDialogOpen,
  setIsTariffsDialogOpen,
}) => (
  <>
    <TrackedDialog isOpen={isTermsDialogOpen} setOpen={setIsTermsDialogOpen}>
      <Stack gap="6">
        <TermsAndConditions
          label={label}
          flowContext={flowContext}
          textualData={textualData}
          offer={selectedOffer}
          offerTermsAndConditions={selectedOfferTermsAndConditions}
        />
      </Stack>
    </TrackedDialog>

    <TrackedDialog isOpen={isContractPeriodDialogOpen} setOpen={setIsContractPeriodDialogOpen}>
      <Stack direction="column" gap={{ initial: 2, lg: 6 }}>
        <Calendar size="large" color="brandGradient" />
        <Heading as="h2" size="S">
          Wat gebeurt er na de vaste looptijd van je contract?
        </Heading>
        <Text>
          Na de einddatum van de vaste looptijd blijven we gewoon energie aan je leveren. Dat doen we dan tegen ons
          variabele tarief van dat moment. Bij een variabel contract kunnen je tarieven maximaal eens in de 3 maanden
          stijgen of dalen. Bij een prijswijziging ontvang je uiterlijk een maand van tevoren persoonlijk bericht. Ben
          je het er niet mee eens? Dan kan je kosteloos opzeggen.
        </Text>
        <Stack as="ul" direction="column" gap="2">
          <Stack as="li" direction="row" gap="2" alignY="center">
            <Stack.Item shrink={false}>
              <DocumentIcon size="medium" color="feedbackSuccess" />
            </Stack.Item>
            <TextLink emphasis="high" color="linkBrand" onClick={() => setIsConditionsDialogOpen(true)}>
              voorwaarden
            </TextLink>
          </Stack>
          <Stack as="li" direction="row" gap="2" alignY="center">
            <Stack.Item shrink={false}>
              <DocumentIcon size="medium" color="feedbackSuccess" />
            </Stack.Item>
            <TextLink emphasis="high" color="linkBrand" onClick={() => setIsTariffsDialogOpen(true)}>
              tarieven
            </TextLink>
          </Stack>
        </Stack>
      </Stack>
    </TrackedDialog>

    <TrackedDialog
      isOpen={isConditionsDialogOpen}
      setOpen={setIsConditionsDialogOpen}
      title={textualData?.salesConditions?.salesConditionsDialog?.value?.title}
      description={<RichText html={textualData?.salesConditions?.salesConditionsDialog?.value?.content} />}>
      <Stack direction="column" gap="2">
        {selectedOfferTermsAndConditions?.map(
          ({ href, title }) =>
            href &&
            href !== 'null' &&
            title && (
              <NavLink key={href} href={href} target="_blank">
                {title}
              </NavLink>
            ),
        )}
      </Stack>
    </TrackedDialog>

    <TrackedDialog
      isOpen={isTariffsDialogOpen}
      setOpen={setIsTariffsDialogOpen}
      title={textualData?.salesConditions?.tariffsDialog?.value?.title}
      description={<RichText html={textualData?.salesConditions?.tariffsDialog?.value?.content} />}>
      <PriceDetailsOverview
        offer={selectedOffer}
        flowContext={flowContext}
        textualData={textualData as SummaryStepFields}
      />
    </TrackedDialog>
  </>
);

interface SummaryDisputeTextProps {
  children?: ReactNode;
}

export const SummaryDisputeText: FC<SummaryDisputeTextProps> = ({ children }) =>
  children ? (
    <Text size="BodyXS" align="center" color="textLowEmphasis">
      {children}
    </Text>
  ) : null;
